/**
 * F10 (#3991) — measuring the origin/CouchDB "split brain".
 *
 * Our reachability probe pings the APP ORIGIN, not CouchDB. So when the origin is reachable but
 * CouchDB is not, the app reads "Online" while sync is in fact dead. The two halves of the app
 * disagree about whether there is a connection.
 *
 * Changing what the probe pings would be a real behavior change with its own risks, so before
 * doing that we want to know how often this actually happens in the wild. This is MEASUREMENT
 * ONLY: no state machine changes. When sync gives up and commits a debounced transition to
 * Offline while the origin probe still says Online, we ping CouchDB once and report what we
 * found. A follow-up can then decide whether the probe target should change.
 */

/** The sessionStorage latch. One measurement per session is enough. */
export const SPLIT_BRAIN_LATCH_KEY = 'splitBrainReported';

/** Should we ping CouchDB to find out whether this Offline-while-Online disagreement is a real
 *  split brain?
 *
 *  At most once per session on purpose: this fires on a failure path, and a device with a
 *  flapping connection would otherwise send an unbounded stream of events and hammer CouchDB
 *  with probes. One sample per session is plenty to measure how common the split is. */
export function shouldProbeForSplitBrain(input: {
  /** Does the origin probe currently say we are online? (Sync has already said Offline — that is
   *  what triggered this check.) If this is false too, both halves agree and there is no
   *  disagreement worth measuring. */
  connectionOnline: boolean;
  /** Have we already taken our one measurement this session? */
  alreadyReportedThisSession: boolean;
}): boolean {
  return input.connectionOnline && !input.alreadyReportedThisSession;
}
